import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItem,
    ListItemText,
    Typography,
} from '@mui/material';
import queueService from '../../services/queueService';

/**
 * Conferma di eliminazione di una coda di lavoro (piano «code sui ruoli», Task 16).
 *
 * L'eliminazione e' irreversibile per chi la subisce: prima di chiedere conferma, il
 * dialogo mostra quali GRUPPI aprivano questa coda, quante persone ci stanno dentro e con
 * quante code restano dopo (`GET /v1/queues/{id}/impact`, per id perche' e' la chiave con cui
 * la UI gia' identifica una coda).
 *
 * Fase F6: prima nominava i RUOLI. Le code non appartengono piu' al ruolo ma al gruppo, e un
 * avviso che nomina entita' che su questa coda non decidono piu' nulla direbbe il falso
 * proprio prima di un'azione irreversibile.
 *
 * Il caso che il dialogo esiste per catturare e' `remainingQueues === 0`: un gruppo che
 * smetterebbe di vedere del tutto per quella via, non solo di vedere meno -- e' l'unica riga
 * messa in evidenza come avviso, le altre restano informative.
 *
 * Non mostra un conteggio delle attivita' aperte sulla coda: derivarlo richiederebbe
 * decidere quali stati contano come "aperti" per questo avviso, una scelta di prodotto
 * fuori dall'ambito di questo task -- un numero indovinato sarebbe peggio di nessun numero.
 */
export default function DeleteQueueDialog({ queueId, queueName, open, onClose, onConfirm }) {
    const [impatto, setImpatto] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open || !queueId) {
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        queueService
            .impatto(queueId)
            .then((righe) => {
                if (!cancelled) {
                    setImpatto(righe);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setError('Impossibile calcolare chi perde visibilità su questa coda.');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [open, queueId]);

    const gruppiAZero = (impatto || []).filter((gruppo) => gruppo.remainingQueues === 0);

    // La conferma resta bloccata finche' l'impatto non e' stato caricato con successo: senza
    // questo, un fetch fallito (rete caduta, 500...) rimette il bottone abilitato senza che
    // l'utente abbia mai visto chi perde cosa -- esattamente l'esito che questo dialogo
    // esiste per evitare. `impatto === null` copre anche il caso "non ancora arrivato".
    const confermaBloccata = loading || Boolean(error) || impatto === null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Eliminare la coda «{queueName}»?</DialogTitle>
            <DialogContent>
                {loading && <CircularProgress size={24} />}
                {error && <Alert severity="error">{error}</Alert>}
                {!loading && !error && impatto && (
                    impatto.length === 0 ? (
                        <Typography>Nessun gruppo apre questa coda: nessuno perde visibilità.</Typography>
                    ) : (
                        <>
                            <Typography sx={{ mb: 1 }}>
                                Questi gruppi aprono questa coda:
                            </Typography>
                            <List dense>
                                {impatto.map((gruppo) => (
                                    <ListItem key={gruppo.groupId} disableGutters>
                                        <ListItemText
                                            primary={gruppo.groupName}
                                            secondary={`${gruppo.userCount} persone, resta con ${gruppo.remainingQueues} code`}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                            {gruppiAZero.length === 0 ? (
                                <Alert severity="info" sx={{ mt: 1 }}>
                                    Nessun gruppo resta a zero code: nessuno smetterà di vedere del tutto.
                                </Alert>
                            ) : (
                                gruppiAZero.map((gruppo) => (
                                    <Alert severity="warning" key={gruppo.groupId} sx={{ mt: 1 }}>
                                        {gruppo.groupName} resterà senza nessuna coda: {gruppo.userCount} persone
                                        {' '}smetteranno di vedere del tutto.
                                    </Alert>
                                ))
                            )}
                        </>
                    )
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Annulla</Button>
                <Button color="error" variant="contained" onClick={onConfirm} disabled={confermaBloccata}>
                    Elimina
                </Button>
            </DialogActions>
        </Dialog>
    );
}

DeleteQueueDialog.propTypes = {
    queueId: PropTypes.string,
    queueName: PropTypes.string,
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
};
